import { Border, Coordinates } from "@giga-user-fern/api/types/api";
import {
	VideoEdits,
	ZoomEdit,
} from "@giga-user-fern/api/types/api/resources/video";
import { VideoClip } from "../../../../types/guide";
import { getBrandingColor } from "../../../../utils/styleUtils";
import { interpolate } from "../../../video_effects/interpolations";
import {
	renderAnimatedBorder,
	renderGlassFrame,
	renderMacFrame,
	renderMobileFrame,
	renderSolidBorder,
} from "../../canvasBorders";
import { drawIntro, printIntroClipTitle } from "../../canvasIntro";
import { drawOutro } from "../../canvasOutro";
import {
	applyCircleWipe,
	applyColorWipeTransition,
	applyCornerWipe,
	applyRainbowWipe,
	applyStackTransition,
	TransitionParams,
} from "../../canvasTransitions";
import {
	drawBoxShadow,
	drawRoundedRect,
	getCorrectedPositionForBorders,
} from "../../canvasUtils";
import { RendererType, ZOOM_TRANSITION_TIME } from "../../constants";
import { CanvasAssets } from "../assets/CanvasAssets";
import { RendererSubtitles } from "../CanvasRenderer";
import { CanvasCoordinates } from "../coordinates/CanvasCoordinates";
import { CanvasTimer } from "../timer/CanvasTimer";
import { CanvasElements } from "./CanvasElements";

export type CanvasFramerProps = {
	canvas?: HTMLCanvasElement;
	clips?: VideoClip[];
	tempCanvas?: HTMLCanvasElement;
	paused?: boolean;
};

type RenderResult = {
	zoom?: ZoomRender | null;
};

export type ZoomRender = {
	zoomFactor: number;
	zoomCenter: Coordinates;
	e?: number;
	f?: number;
};

const INTERPOLATION_METHOD = "easeOutQuart";

export const dummyFramerProps: CanvasFramerProps = {};

export class CanvasFramer {
	/**
	 * This class is used to orchestrate rendering frames of the canvas
	 * Primarily, it will be used to render a canvas frame based on a passed value of current time.
	 */

	videoEdits: VideoEdits;
	props: CanvasFramerProps;
	timer: CanvasTimer;
	assets: CanvasAssets;
	elements: CanvasElements;
	coordinates: CanvasCoordinates;
	rendererType: RendererType;
	//more assets
	sortedZooms: ZoomEdit[];

	constructor(
		videoEdits: VideoEdits,
		props: CanvasFramerProps,
		timer: CanvasTimer,
		assets: CanvasAssets,
		coordinates: CanvasCoordinates,
		rendererType: RendererType,
		subtitles?: RendererSubtitles,
	) {
		this.videoEdits = videoEdits;
		this.props = { ...props };
		this.timer = timer;
		this.assets = assets;
		this.coordinates = coordinates;
		this.rendererType = rendererType;

		//more assets
		if (this.videoEdits.zooms) {
			this.sortedZooms = Array.from(this.videoEdits.zooms).sort(
				(a, b) => a.startTime - b.startTime,
			);
		} else {
			this.sortedZooms = [];
		}

		//framer helpers
		this.elements = new CanvasElements(
			videoEdits,
			{
				canvas: props.canvas,
				tempCanvas: props.tempCanvas,
				paused: props.paused,
			},
			assets,
			coordinates,
			rendererType,
			subtitles,
		);
	}

	initialize = () => {};

	setDimensions = () => {};

	//#region CHARACTER functions

	hasIntroClip = () => {
		return (
			this.videoEdits?.intro?.visible &&
			this.videoEdits.intro.type === "video"
		);
	};

	hasOutroClip = () => {
		return (
			this.videoEdits?.outro?.visible &&
			this.videoEdits.outro.type === "video"
		);
	};

	//#endregion

	//#region RENDER functions

	render: (time: number) => RenderResult = (time: number) => {
		const res: RenderResult = {};

		const ctx = this.props.canvas?.getContext(
			"2d",
		) as CanvasRenderingContext2D;

		if (!ctx || !this.props.canvas) {
			// console.error("returning prematurely", ctx, this.props.canvas);
			return res;
		}

		const intro = this.videoEdits?.intro || undefined;
		const outro = this.videoEdits?.outro || undefined;

		let introDuration = intro?.duration ?? 0;

		if (intro?.transition?.type !== undefined) {
			introDuration += intro?.transition?.duration ?? 0;
		}

		const outroDuration = outro?.duration;

		// Clear previous frame
		ctx.save();
		// ADDED FOR SKIA

		ctx.clearRect(
			0,
			0,
			this.coordinates.canvasWidth,
			this.coordinates.canvasHeight,
		);

		// Clear previous zoom, if any
		ctx.resetTransform();

		//check if we should render intro
		if (intro?.visible && introDuration && time < introDuration) {
			//Render the intro

			// construct imageDataMap
			if (this.hasIntroClip()) {
				this.renderBookendsFrame("intro", time);

				printIntroClipTitle(ctx, time, intro, {
					width: this.coordinates.canvasWidth,
					height: this.coordinates.canvasHeight,
					background: this.assets.intro.bgImage?.src || null,
					getFont: this.assets.getFont,
					imageDataMap: null,
				});
			} else {
				drawIntro(this.props.canvas, intro, {
					width: this.coordinates.canvasWidth,
					height: this.coordinates.canvasHeight,
					background: this.assets.intro.bgImage?.src || null,
					imageDataMap: this.assets.intro.imageElementsMap || null,
					getFont: this.assets.getFont,
				});

				//TODO skia: Should this be there??
				// drawElements();

				if (
					intro.transition &&
					time >=
						this.timer.getScreenclipStartTime() -
							intro.transition.duration &&
					time <= this.timer.getScreenclipStartTime()
				) {
					const scale = interpolate(
						{
							x:
								this.timer.getScreenclipStartTime() -
								intro.transition.duration,
							y: 0.8,
						},
						{
							x: this.timer.getScreenclipStartTime(),
							y: 1,
						},
						time,
						"easeInOutSin",
					);

					const transitionParams: TransitionParams = {
						ctx: ctx,
						time: time,
						transitionDuration: intro.transition.duration,
						keyTime: this.timer.getScreenclipStartTime(),
						canvasWidth: this.coordinates.canvasWidth,
						canvasHeight: this.coordinates.canvasHeight,
						transition: intro.transition,
						backgroundVisible: this.videoEdits.background?.visible,
						mode: "in",
						drawUnderlying: (
							t: number,
							opacity?: number,
							ignoreEdits?: boolean,
						) => {
							res.zoom = this.drawScreenclip(
								t,
								opacity ?? 1,
								this.videoEdits.background?.visible ? scale : 1,
								ignoreEdits,
							);
						},
					};

					switch (intro.transition.type) {
						case "circleWipe":
							res.zoom = applyCircleWipe(transitionParams);
							break;
						case "rainbowWipe":
							res.zoom = applyRainbowWipe({
								...transitionParams,
								customClip: () => {
									res.zoom = this.drawScreenclip(
										time,
										1,
										this.videoEdits.background?.visible
											? scale
											: 1,
									);
								},
							});
							break;
						case "cornerWipe":
							res.zoom = applyCornerWipe({
								...transitionParams,
								customClip: () => {
									res.zoom = this.drawScreenclip(
										time,
										1,
										this.videoEdits.background?.visible
											? scale
											: 1,
									);
								},
							});
							break;
						case "stack":
							res.zoom = applyStackTransition({
								...transitionParams,
								drawUnderlying: () => {
									// override the drawUnderlying function here because we want to draw the intro and not the screenclip first
									if (this.props.canvas) {
										drawIntro(this.props.canvas, intro, {
											width: this.coordinates.canvasWidth,
											height: this.coordinates
												.canvasHeight,
											background:
												this.assets.intro.bgImage
													?.src || null,
											imageDataMap:
												this.assets.intro
													.imageElementsMap || null,
											getFont: this.assets.getFont,
										});
									}
								},
								customClip: () => {
									res.zoom = this.drawScreenclip(
										time,
										1,
										1,
										true,
									);
								},
							});
							break;
						case "colorWipe":
							res.zoom =
								applyColorWipeTransition(transitionParams);
							break;
						default:
							break;
					}
				}
			}
		}

		//check if we should render the outro
		else if (
			outro?.visible &&
			outroDuration &&
			time >= this.timer.getScreenclipEndTime()
		) {
			if (
				outro.transition?.duration &&
				time >= this.timer.getScreenclipEndTime() &&
				time <=
					this.timer.getScreenclipEndTime() +
						outro.transition?.duration
			) {
				const transition_duration = outro.transition.duration;

				const transitionStartAbsoluteTime =
					this.timer.getScreenclipEndTime();
				const transitionEndAbsoluteTime =
					transitionStartAbsoluteTime + transition_duration;

				const scale = interpolate(
					{
						x: transitionStartAbsoluteTime,
						y: 1,
					},
					{
						x: transitionEndAbsoluteTime,
						y: 0.7,
					},
					time,
					"easeInOutSin",
				);

				if (outro.type === "video") {
					this.renderBookendsFrame(
						"outro",
						time - this.timer.getScreenclipEndTime(),
					);
				} else {
					drawOutro(this.props.canvas, outro, {
						width: this.coordinates.canvasWidth,
						height: this.coordinates.canvasHeight,
						background: this.assets.outro.bgImage?.src || null,
						imageDataMap:
							this.assets.outro.imageElementsMap || null,
						getFont: this.assets.getFont,
					});
				}

				const transitionParams: TransitionParams = {
					ctx: ctx,
					time: time,
					transitionDuration: outro.transition.duration,
					keyTime: this.timer.getScreenclipEndTime(),
					canvasWidth: this.coordinates.canvasWidth,
					canvasHeight: this.coordinates.canvasHeight,
					transition: outro.transition,
					backgroundVisible: this.videoEdits.background?.visible,
					mode: "out",
					drawUnderlying: (time, opacity, ignoreEdits) => {
						res.zoom = this.drawScreenclip(
							time,
							opacity,
							this.videoEdits.background?.visible &&
								outro.transition?.type !== "stack"
								? scale
								: 1,
							ignoreEdits,
						);
					},
					customClip: () => {
						if (this.props.canvas) {
							drawOutro(this.props.canvas, outro, {
								width: this.coordinates.canvasWidth,
								height: this.coordinates.canvasHeight,
								background:
									this.assets.outro.bgImage?.src || null,
								imageDataMap:
									this.assets.outro.imageElementsMap || null,
								getFont: this.assets.getFont,
							});
						}
					},
				};

				switch (outro.transition.type) {
					case "circleWipe":
						res.zoom = applyCircleWipe(transitionParams);
						break;
					case "rainbowWipe":
						res.zoom = applyRainbowWipe({
							...transitionParams,
						});
						break;
					case "cornerWipe":
						res.zoom = applyCornerWipe({
							...transitionParams,
						});

						break;
					case "stack":
						res.zoom = applyStackTransition({
							...transitionParams,
							drawUnderlying: (time) => {
								res.zoom = this.drawScreenclip(
									time,
									1,
									1, // no scaling for stack transition
									false,
								);
							},
						});
						break;
					case "colorWipe":
						res.zoom = applyColorWipeTransition({
							...transitionParams,
							drawUnderlying: () => {
								if (this.props.canvas) {
									drawOutro(this.props.canvas, outro, {
										width: this.coordinates.canvasWidth,
										height: this.coordinates.canvasHeight,
										background:
											this.assets.outro.bgImage?.src ||
											null,
										imageDataMap:
											this.assets.outro
												.imageElementsMap || null,
										getFont: this.assets.getFont,
									});
								}
							},
							customClip: () => {
								this.drawScreenclip(
									time,
									1,
									this.videoEdits.background?.visible
										? scale
										: 1,
								);
							},
						});

						break;
					default:
						break;
				}
			} else if (outro.type === "video") {
				this.renderBookendsFrame(
					"outro",
					time - this.timer.getScreenclipEndTime(),
				);
			} else
				drawOutro(this.props.canvas, outro, {
					width: this.coordinates.canvasWidth,
					height: this.coordinates.canvasHeight,
					background: this.assets.outro.bgImage?.src || null,
					imageDataMap: this.assets.outro.imageElementsMap || null,
					getFont: this.assets.getFont,
				});
		} else {
			//render the video here.
			res.zoom = this.drawScreenclip(time);
		}

		ctx.restore();
		return res;
	};

	private drawScreenclip: (
		time: number,
		opacity?: number,
		scale?: number,
		ignoreEdits?: boolean,
	) => ZoomRender | null = (
		time,
		opacity = 1,
		scale = 1,
		ignoreEdits = false,
	) => {
		//Render the video frame

		let res: ZoomRender | null = null;

		const { canvas } = this.props;

		if (!canvas) {
			// console.error("can't render canvas");
			return res;
		}

		const ctx = canvas.getContext("2d") as CanvasRenderingContext2D;

		//perform zoom

		if (!ignoreEdits) {
			res = this.scaleCanvas(time);
		}

		//draw background
		ctx.drawImage(
			this.assets.background.src,
			0,
			0,
			canvas.width,
			canvas.height,
		);

		this.renderScreenclipFrame(
			this.timer.getUnadjustedTime(time),
			opacity,
			scale,
		);

		// TODO skia: This is not a proper call. Fix this.

		if (
			!ignoreEdits &&
			this.props.canvas &&
			this.videoEdits.elements &&
			this.props.tempCanvas
		) {
			this.elements.renderElements(this.timer.getUnadjustedTime(time));
		}

		//render subtitles

		this.elements.renderSubtitles(this.timer.getUnadjustedTime(time));

		return res;
	};

	private renderBookendsFrame = (slide: "intro" | "outro", time: number) => {
		let bookendsVideo: HTMLVideoElement | null = null;

		if (slide === "intro") {
			bookendsVideo = this.assets.intro.bgVideo?.frame(
				time,
			) as HTMLVideoElement;
		} else {
			bookendsVideo = this.assets.outro.bgVideo?.frame(
				time,
			) as HTMLVideoElement;
		}

		if (!bookendsVideo) return;

		const canvas = this.props.canvas;
		const ctx = canvas?.getContext("2d") as CanvasRenderingContext2D;

		if (!bookendsVideo || !canvas) return;

		// Clip rounded rectangle path for the video
		ctx.save();
		ctx.beginPath();

		let vidWidth =
			slide === "intro"
				? this.videoEdits.intro?.naturalWidth
				: this.videoEdits.outro?.naturalWidth;
		let vidHeight =
			slide === "intro"
				? this.videoEdits.intro?.naturalHeight
				: this.videoEdits.outro?.naturalHeight;

		vidWidth = vidWidth || 1920;
		vidHeight = vidHeight || 1080;

		ctx.drawImage(bookendsVideo, 0, 0, canvas.width, canvas.height);
	};

	private renderScreenclipFrame = (
		time: number,
		opacity = 1,
		scale = 1,
		xPos: number | null | undefined = null,
		yPos: number | null | undefined = null,
	) => {
		/**
		 * @param opacity @param scale : used for fade in/out entry and exit. this scale not to be confused with zoom scale!
		 */
		const canvas = this.props.canvas;
		const ctx = canvas?.getContext("2d") as CanvasRenderingContext2D;

		if (!canvas) {
			// console.error("can't render canvas!");
			return;
		}

		let source: HTMLVideoElement = this.assets.screenclip.frame(
			time,
		) as HTMLVideoElement;

		//This logic determines which source needs to be shown on the screen.
		if (this.props.clips?.length) {
			const videoTime = this.timer.timelineToVideoTime(time);
			const sourceId = videoTime.sourceId;
			const sourceClip = this.assets.sources?.find(
				(s) => s.id === sourceId,
			);

			// if (sourceClip?.ref.current) source = sourceClip.ref.current;
			source = sourceClip?.frame(time) as HTMLVideoElement;
		}

		const bgEdits = this.videoEdits.background;
		const borderEdits = this.videoEdits.border;

		// ctx.clearRect(0, 0, canvas.width, canvas.height);

		let borderRadius = this.videoEdits.background?.visible
			? (this.assets.screenclip.naturalHeight *
					(this.videoEdits.background?.borderRadius || 0)) /
				100
			: 0;

		if (this.videoEdits.crop) {
			borderRadius *= this.videoEdits.crop.size[1];
		}

		const actualVideoRenderWidth =
			this.coordinates.videoRenderWidth * scale;
		const actualVideoRenderHeight =
			this.coordinates.videoRenderHeight * scale;

		let actualXPos = (canvas.width - actualVideoRenderWidth) * 0.5;
		let actualYPos = (canvas.height - actualVideoRenderHeight) * 0.5;

		const { correctedXPos, correctedYPos } = getCorrectedPositionForBorders(
			actualXPos,
			actualYPos,
			actualVideoRenderWidth,
			actualVideoRenderHeight,
			borderEdits,
		);

		actualXPos = correctedXPos;
		actualYPos = correctedYPos;

		if (xPos !== null && xPos !== undefined) actualXPos = xPos;
		if (yPos !== null && yPos !== undefined) actualYPos = yPos;

		// 1. Apply opacity and save context
		ctx.globalAlpha = opacity;
		ctx.save();

		// 2. Apply shadow for the frame
		drawBoxShadow(
			ctx,
			actualXPos,
			actualYPos,
			actualVideoRenderWidth,
			actualVideoRenderHeight,
			bgEdits?.shadow || 0,
			borderEdits?.type || "none",
			borderRadius <= 0 ? 0 : borderRadius,
		);

		// 3. Apply borders with the current transform intact
		this.renderBorders(
			ctx,
			actualXPos,
			actualYPos,
			actualVideoRenderWidth,
			actualVideoRenderHeight,
			borderRadius,
			borderEdits,
			time,
			"pre",
		);

		// 4. Make the clipping region
		drawRoundedRect(
			ctx,
			actualXPos,
			actualYPos,
			actualVideoRenderWidth,
			actualVideoRenderHeight,
			borderRadius,
			borderEdits?.type === "browser" ? 0 : borderRadius,
			borderRadius,
		);

		ctx.clip();

		// 4. Render the video within the clipped region
		if (this.videoEdits?.crop) {
			const crop = this.videoEdits.crop;
			// The natural height and width are the original dimensions of the video
			const sx = crop.position[0] * this.assets.screenclip.naturalWidth; // Source x - start of crop in video coordinates
			const sy = crop.position[1] * this.assets.screenclip.naturalHeight; // Source y - start of crop in video coordinates
			const sWidth = crop.size[0] * this.assets.screenclip.naturalWidth; // Source width - width of crop in video coordinates
			const sHeight = crop.size[1] * this.assets.screenclip.naturalHeight; // Source height - height of crop in video coordinates

			ctx.drawImage(
				source,
				sx,
				sy,
				sWidth,
				sHeight,
				actualXPos,
				actualYPos,
				actualVideoRenderWidth,
				actualVideoRenderHeight,
			);
		} else {
			ctx.drawImage(
				source,
				actualXPos,
				actualYPos,
				actualVideoRenderWidth,
				actualVideoRenderHeight,
			);
		}

		// 5. Restore context (which also removes the clipping)
		ctx.restore();
		this.renderBorders(
			ctx,
			actualXPos,
			actualYPos,
			actualVideoRenderWidth,
			actualVideoRenderHeight,
			borderRadius,
			borderEdits,
			time,
			"post",
		);
	};

	private renderBorders = (
		ctx: CanvasRenderingContext2D,
		xPos: number,
		yPos: number,
		videoRenderWidth: number,
		videoRenderHeight: number,
		borderRadius: number,
		borderEdits: Border | undefined,
		time?: number,
		side: "pre" | "post" = "pre", // pre: before video, post: after video (post is used for the mobile frame which is drawn after and over the video)
	) => {
		if (side === "pre") {
			switch (borderEdits?.type) {
				case "solid":
					renderSolidBorder(
						ctx,
						xPos,
						yPos,
						videoRenderWidth,
						videoRenderHeight,
						borderEdits?.borderWidth || 0,
						borderEdits?.color || getBrandingColor(),
						borderRadius,
						0,
					);
					break;
				case "browser":
					renderMacFrame(
						(ctx, path, x, y, scale) =>
							this.assets.drawSVGPath(ctx, path, x, y, scale),
						ctx,
						xPos,
						yPos,
						videoRenderWidth,
						videoRenderHeight,
						borderEdits?.borderWidth || 0,
						borderRadius,
						borderEdits?.color || getBrandingColor(),
						this.videoEdits.border?.title || "My Video Title",
					);
					break;
				case "glass":
					renderGlassFrame(
						ctx,
						xPos,
						yPos,
						videoRenderWidth,
						videoRenderHeight,
						borderEdits?.borderWidth || 0,
						borderEdits?.color || getBrandingColor(),
						borderRadius,
					);
					break;
				case "animated":
					renderAnimatedBorder(
						ctx,
						xPos,
						yPos,
						videoRenderWidth,
						videoRenderHeight,
						time || 0,
						borderEdits?.borderWidth || 0,
						borderEdits?.color || getBrandingColor(),
						borderRadius,
					);

					break;
			}
		} else {
			switch (borderEdits?.type) {
				case "notch":
					renderMobileFrame(
						ctx,
						xPos,
						yPos,
						videoRenderWidth,
						videoRenderHeight,
						borderRadius,
						borderEdits?.color || getBrandingColor(),
						borderEdits?.borderWidth || 0,
					);
					break;
			}
		}
	};

	//#endregion

	//#region GEOMTERY

	scaleCanvas: (time: number) => ZoomRender | null = (time) => {
		let zoomFactor = 1;
		let zoomCenter: Coordinates = { x: 0.5, y: 0.5 };

		let e = 0;
		let f = 0;

		const canvas = this.props.canvas;

		if (!canvas) return { zoomFactor, zoomCenter };
		const ctx = canvas?.getContext("2d") as CanvasRenderingContext2D;

		let zoomEdit: ZoomEdit | null = null;

		const currentTime = this.timer.timelineToVideoTime(time).time;

		let zoomRes: ZoomRender | null = null;

		let prevFactor = 1;
		let nextFactor = 1;
		let nextCenter = { x: 0.5, y: 0.5 };

		if (!this.videoEdits.zooms) return { zoomFactor, zoomCenter };

		const zooms = this.sortedZooms;

		for (let i = 0; i < zooms.length; i++) {
			const currentZoom = zooms[i];

			if (
				currentZoom.startTime <= currentTime &&
				currentTime <= currentZoom.endTime
			) {
				zoomEdit = currentZoom;

				if (i > 0 && currentZoom.startTime === zooms[i - 1].endTime) {
					prevFactor = zooms[i - 1].zoomFactor;
				}

				if (
					i < zooms.length - 1 &&
					currentZoom.endTime === zooms[i + 1].startTime
				) {
					nextFactor = zooms[i + 1].zoomFactor;
					nextCenter = zooms[i + 1].zoomCenter;
				}

				break;
			}
		}

		if (zoomEdit) {
			const transitionTime = Math.min(
				zoomEdit.transitionTime || ZOOM_TRANSITION_TIME,
				(zoomEdit.endTime - zoomEdit.startTime) / 2,
			);

			//timings
			const zoomInFinishTime = zoomEdit.startTime + transitionTime;
			const zoomOutStartTime = zoomEdit.endTime - transitionTime;

			//zoom
			const currZoom = zoomEdit.zoomFactor;
			const currCenter = zoomEdit.zoomCenter;

			if (
				zoomEdit.startTime < currentTime &&
				currentTime <= zoomInFinishTime
			) {
				//The zoom in phase
				if (prevFactor === 1)
					zoomFactor = this.interpolateZoom(
						zoomEdit.startTime,
						zoomInFinishTime,
						prevFactor,
						currZoom,
						currentTime,
					);
				else zoomFactor = currZoom;

				zoomCenter = currCenter;
			} else if (
				zoomInFinishTime < currentTime &&
				currentTime <= zoomOutStartTime
			) {
				//The keep zoom constant phase
				zoomFactor = currZoom;
				zoomCenter = currCenter;
			} else if (
				zoomOutStartTime < currentTime &&
				currentTime <= zoomEdit.endTime
			) {
				//The zoom out phase
				zoomFactor = this.interpolateZoom(
					zoomOutStartTime,
					zoomEdit.endTime,
					currZoom,
					nextFactor,
					currentTime,
				);

				if (
					nextCenter.x === 0.5 &&
					nextCenter.y === 0.5 &&
					nextFactor === 1
				)
					zoomCenter = currCenter;
				else
					zoomCenter = this.interpolateCenter(
						currCenter,
						nextCenter,
						zoomOutStartTime,
						zoomEdit.endTime,
						currentTime,
					);
			}

			const zoom_canvas_coords =
				this.coordinates.fractionalCoordsToCanvasCoords(
					zoomCenter,
					true,
				);
			e = zoom_canvas_coords.x * (1 - zoomFactor);
			f = zoom_canvas_coords.y * (1 - zoomFactor);

			zoomRes = { zoomFactor, zoomCenter, e, f };
		} else {
			zoomRes = null;
		}

		// Apply transformations for zoom effect
		ctx.save();

		// Set the transformations using setTransform
		ctx.setTransform(zoomFactor, 0, 0, zoomFactor, e, f);

		return zoomRes;
	};

	interpolateCenter = (
		startCenter: Coordinates,
		endCenter: Coordinates,
		startTime: number,
		endTime: number,
		currentTime: number,
	): Coordinates => {
		const startX = { y: startCenter.x, x: startTime };
		const endX = { y: endCenter.x, x: endTime };
		const X = interpolate(startX, endX, currentTime, INTERPOLATION_METHOD);

		const startY = { y: startCenter.y, x: startTime };
		const endY = { y: endCenter.y, x: endTime };
		const Y = interpolate(startY, endY, currentTime, INTERPOLATION_METHOD);

		return { x: X, y: Y };
	};

	interpolateZoom: (
		startTime: number,
		endTime: number,
		startZoom: number,
		endZoom: number,
		currentTime: number,
	) => number = (startTime, endTime, startZoom, endZoom, currentTime) => {
		const start = { y: startZoom, x: startTime };
		const end = { y: endZoom, x: endTime };

		return interpolate(start, end, currentTime, INTERPOLATION_METHOD);
	};
}
